import { FC, memo, ReactElement, useCallback, useRef, useState } from 'react';
import loadable from '@loadable/component';
import cn from 'classnames';

import ServicePageTitle from '@Components/ServicePageTitle';
import Breadcrumbs from '@UI/Breadcrumbs';
import TemplateMain, { TemplateMainProps } from '@Templates/TemplateMain';
import { BreadcrumbData } from '@Types/Breadcrumbs';
import { NavigationItemData } from '@Types/NavigationItem';
import useScrollPosition from '@Hooks/useScrollPosition';
import styles from './TemplateService.module.css';

export interface TemplateServiceProps extends TemplateMainProps {
  breadcrumbs?: BreadcrumbData[];
  title?: string;
  navigation?: NavigationItemData[];
  banner?: ReactElement;
}

const InformationTabsNavigation = loadable(() => import('@Components/InformationTabsNavigation'));

const TemplateService: FC<TemplateServiceProps> = (props) => {
  const { children, breadcrumbs = [], title, navigation = [], banner, ...restProps } = props;
  const [hidden, setHidden] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const hasNavigation = navigation.length > 0;
  const refContent = useRef<HTMLDivElement>(null);
  const refBreadCrumbs = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!refContent.current || !banner) return;

    const { top } = refContent.current.getBoundingClientRect();
    const { y: borderOfBreadCrumbs } = refBreadCrumbs.current.getBoundingClientRect();
    setScrolling(borderOfBreadCrumbs < 0);
    setHidden(top < 0);
  }, [banner]);

  useScrollPosition(handleScroll);

  return (
    <TemplateMain {...restProps}>
      {breadcrumbs.length > 0 && (
        <div className={styles.mainWrapper} ref={refBreadCrumbs}>
          <Breadcrumbs className={styles.breadcrumbs} breadcrumbs={breadcrumbs} />
        </div>
      )}

      {banner && (
        <div
          className={cn(styles.banner, styles.mainWrapper, {
            [styles.hidden]: hidden,
            [styles.scrolling]: scrolling,
          })}
        >
          {banner}
        </div>
      )}

      <div className={cn(styles.content, { [styles.withBanner]: banner })} ref={refContent}>
        {(title || hasNavigation) && (
          <div className={styles.mainWrapper}>
            {title && <ServicePageTitle title={title} />}
            {hasNavigation && (
              <InformationTabsNavigation className={styles.menu} navigation={navigation} />
            )}
          </div>
        )}

        {children}
      </div>
    </TemplateMain>
  );
};

export default memo(TemplateService);
